import { useEffect, useState } from 'react';
import { Badge, Group, Loader, Switch } from '@mantine/core';
import {
    formatPayoutTrader,
    PayoutTraderWorkingStatus,
    payoutTraderWorkingStatusColors,
    payoutTraderWorkingStatusMeta,
} from '~/entities/payout';

interface WorkingStatusFieldProps {
    workingStatus?: PayoutTraderWorkingStatus;
    canEdit: boolean;
    isPending: boolean;
    onChange: (workingStatus: PayoutTraderWorkingStatus) => void;
}

const isInWorkStatus = (workingStatus?: PayoutTraderWorkingStatus) =>
    workingStatus === PayoutTraderWorkingStatus.WORKING_STATUS_IN_WORK;

const toWorkingStatus = (checked: boolean): PayoutTraderWorkingStatus =>
    checked ? PayoutTraderWorkingStatus.WORKING_STATUS_IN_WORK : PayoutTraderWorkingStatus.WORKING_STATUS_READY;

export const WorkingStatusField = ({ workingStatus, canEdit, isPending, onChange }: WorkingStatusFieldProps) => {
    const [checked, setChecked] = useState(() => isInWorkStatus(workingStatus));

    useEffect(() => {
        if (isPending) {
            return;
        }

        const nextChecked = isInWorkStatus(workingStatus);

        if (checked !== nextChecked) {
            setChecked(nextChecked);
        }
    }, [checked, isPending, workingStatus]);

    if (canEdit) {
        const inWorkLabel = workingStatus ? payoutTraderWorkingStatusMeta[workingStatus].label : null;

        return (
            <Group
                gap='sm'
                wrap='nowrap'
                align='center'
            >
                <Switch
                    label={inWorkLabel}
                    checked={checked}
                    disabled={isPending}
                    color='brand'
                    onChange={(event) => {
                        const nextChecked = event.currentTarget.checked;
                        const nextStatus = toWorkingStatus(nextChecked);

                        setChecked(nextChecked);
                        onChange(nextStatus);
                    }}
                />

                {isPending && <Loader size='xs' />}
            </Group>
        );
    }

    return (
        <Badge
            fz='xs'
            color={payoutTraderWorkingStatusColors[workingStatus as PayoutTraderWorkingStatus]}
        >
            {formatPayoutTrader.workingStatus(workingStatus)}
        </Badge>
    );
};
